import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, clearable, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — SỔ PHỤ NGÂN HÀNG · QUỸ TIỀN MẶT · DÒNG TIỀN (F22) ══════════════════════════
 * Nguồn sự thật duy nhất cho `/api/v1/so-phu/*`.
 */

const TxnFields = {
  accountId: Uuid,
  txnDate: ISODate,
  type: z.enum(['thu', 'chi']),
  amount: moneyField('Số tiền'),
  description: blankAsAbsent(z.string()),
  counterparty: blankAsAbsent(z.string()),
  objectCode: blankAsAbsent(z.string()),
  /** TK ĐỐI ỨNG — vế không phải tiền. Thứ quyết định phân loại dòng tiền. */
  contraAccount: blankAsAbsent(z.string()),
  postingDate: blankAsAbsent(ISODate),
  note: blankAsAbsent(z.string()),
  /**
   * ⭐ PHIẾU QUẢN TRỊ (DX) — đặt NGAY LÚC LẬP. Trước đây chỉ sửa được sau khi phiếu đã có số, nên
   * mọi phiếu sinh ra ở trạng thái mặc định rồi chờ ai đó nhớ quay lại; quên một lần là một chứng
   * từ sai loại nằm im trong sổ và đẩy sang MISA sai.
   */
  mgmtOnly: z.boolean().optional(),
}

/**
 * ⭐ CÁC Ô AMIS + HOÁ ĐƠN GTGT ĐẶT NGAY LÚC LẬP (`0082` mở chúng ở bước SỬA).
 * ⚠ Trước đây cửa TẠO chỉ nhận 9 ô, nên mọi phiếu có hoá đơn phải LẬP RỒI MỞ RA SỬA mới điền được
 *   số/ngày hoá đơn · VAT · người nhận · KMCP · mã công trình. Một phiếu lập xong quên quay lại là
 *   một dòng trống vĩnh viễn ở tệp Excel nạp MISA — đúng cái mà `0082` dựng ra để bịt, nhưng chỉ
 *   bịt được nửa đường vì bước lập không có mấy ô đó.
 */
const TxnDetailFields = {
  payeeName: blankAsAbsent(z.string().max(200)),
  partyAddress: blankAsAbsent(z.string().max(500)),
  staffCode: blankAsAbsent(z.string().max(50)),
  attachedDocs: blankAsAbsent(z.coerce.number().int().min(0).max(999)),
  expenseItem: blankAsAbsent(z.string().max(50)),
  jobRef: blankAsAbsent(z.string().max(50)),
  payKind: blankAsAbsent(z.enum(['invoice', 'bank', 'other'])),
  hasInvoice: z.boolean().optional(),
  vatRate: blankAsAbsent(z.coerce.number().min(0).max(100)),
  vatAmount: blankAsAbsent(moneyField('Tiền thuế GTGT')),
  acctVat: blankAsAbsent(z.string().max(20)),
  invoiceDate: blankAsAbsent(ISODate),
  invoiceNo: blankAsAbsent(z.string().max(50)),
  goodsGroup: blankAsAbsent(z.string().max(100)),
  amisReason: blankAsAbsent(z.string().max(300)),
  amisEntryDesc: blankAsAbsent(z.string().max(500)),
}

/**
 * ⭐ LẬP PHIẾU KÈM LUÔN PHẦN ĐỐI TRỪ — bản cũ gắn công nợ NGAY trong hộp lập (`Finance.tsx:642`).
 *
 * Trước đợt này rs-erp bắt làm hai bước: lưu phiếu, rồi mở hộp 🔗 Đối trừ. Kế toán nhận sao kê về
 * và biết NGAY dòng tiền đó trả hoá đơn nào — tách làm hai bước là cơ hội để quên bước hai, và một
 * dòng thu chưa đối trừ thì công nợ khách vẫn treo nguyên dù tiền đã về.
 *
 * ⚠ HAI Ô RIÊNG, KHÔNG gộp một: bên THU cấn được NHIỀU hoá đơn (`allocations` — khách chuyển một
 *   lần cho ba hoá đơn là chuyện thường), bên CHI chỉ MỘT chứng từ mua (`apInvoiceId`) vì tiền chi
 *   đã lan xuống khoản chi phí qua dây chuyền ĐNTT — chia lại là trừ công nợ hai lần.
 */
/** Số chứng từ DỰ KIẾN của hộp lập phiếu + kiểm trùng số gõ tay (bản cũ `Finance.tsx:630`, `:1935-1941`). */
export const VoucherPreviewQuery = z.object({
  accountId: Uuid,
  type: z.enum(['thu', 'chi']),
  txnDate: ISODate,
  mgmtOnly: z.enum(['true', 'false']).optional(),
  voucherNo: blankAsAbsent(z.string().trim()),
})
export type VoucherPreviewQueryT = z.infer<typeof VoucherPreviewQuery>

export const CreateTxnBody = z.object({
  ...TxnFields,
  ...TxnDetailFields,
  allocations: z.array(z.object({ invoiceId: Uuid, amount: moneyField('Số tiền cấn') }))
    .max(50, 'Tối đa 50 hóa đơn trong một phiếu — tách phiếu ra.')
    .optional(),
  apInvoiceId: blankAsAbsent(Uuid),
  matchNote: blankAsAbsent(z.string()),
  /**
   * ⭐ PHIẾU THU 131 KHÔNG GẮN HOÁ ĐƠN NÀO ⇒ CẢ PHIẾU THÀNH CỌC của khách này (bản cũ `Finance.tsx:3502-3506`, người dùng
   *   chốt 17/10). Màn gửi khi người lập chọn khách từ danh mục; loại trừ với `allocations` (phiếu có cấn hoá đơn thì phần
   *   dư tự thành cọc của khách hoá đơn đó).
   */
  advanceCustomerId: blankAsAbsent(Uuid),
  /**
   * ⭐ BÁO NỢ / PHIẾU CHI TRẢ MỘT PHIẾU ĐNTT (bản cũ `Finance.tsx:677`, user 08/08). Lập phiếu + ghi
   *   nhận đã chi + khớp dòng sổ với phiếu — MỘT giao dịch. Trước đây phải bỏ hộp, sang tab Chi tiền
   *   làm lại. Đi kèm SỐ BẢN phiếu (A4): ai vừa duyệt/chi phiếu đó ở màn khác thì ăn 409.
   * ⚠ Loại trừ với `apInvoiceId` / `allocations`: phiếu ĐNTT gắn chứng từ mua thì `markPaid` đã tự
   *   lan tiền xuống chứng từ đó — đối trừ thêm lần nữa là trừ công nợ hai lần.
   */
  paymentRequestId: blankAsAbsent(Uuid),
  paymentRequestVersion: z.number().int().positive().optional(),
  /**
   * ⚠ ĐỐI TƯỢNG TRÊN PHIẾU KHÁC NCC CỦA ĐNTT (C9 ③, bản cũ `Approvals.tsx:6245-6258`, user 18/08): chi cho đối tượng khác
   *   thì công nợ ghi sang đối tượng khác. Bản cũ HỎI LẠI rồi cho đi (trả hộ / đổi pháp nhân xuất HĐ). Máy chủ từ chối
   *   khi hai MST khác nhau, TRỪ KHI hộp đã hỏi và người lập bấm xác nhận — cờ này là dấu của lần xác nhận đó.
   */
  payeeMismatchOk: z.boolean().optional(),
  /**
   * ⭐ CHI VƯỢT ĐÃ XÁC NHẬN (bản cũ `Finance.tsx:762` "Phần dư … sẽ KHÔNG gắn vào phiếu này", anh chốt 23/10): số tiền phiếu LỚN
   *   hơn phần còn phải trả của ĐNTT (`paymentRequestId`) / chứng từ mua (`apInvoiceId`) ⇒ chỉ cấn đúng phần còn lại, dòng sổ giữ
   *   đủ số theo sao kê, phần dư để kế toán xử lý riêng. Vắng cờ ⇒ máy chủ từ chối chi vượt như trước.
   */
  overpayOk: z.boolean().optional(),
  /**
   * ⭐ CHUYỂN NỘI BỘ ⇒ TỰ SINH VẾ CẶP (bản cũ `Finance.tsx:716`, user 10/08): TK đối ứng 111 / 112 thì lập luôn phiếu ngược
   *   chiều ở tài khoản này và ghép đôi, cùng giao dịch. Bỏ trống ⇒ chỉ lập một vế (bộ rà phiếu đôi sẽ nhắc).
   */
  pairAccountId: blankAsAbsent(Uuid),
  /** Số chứng từ GÕ TAY (bản cũ `Finance.tsx:1935`). Trống ⇒ máy cấp nối tiếp dãy của tháng chứng từ. */
  voucherNo: blankAsAbsent(z.string().trim().max(30, 'Số chứng từ tối đa 30 ký tự.')),
  /**
   * ⛽ LÀ PHIẾU ĐỔ DẦU (bản cũ `Finance.tsx:716-741`, user 20/08): phiếu CHI mua dầu máy phát vỏ lạnh ⇒ sinh luôn dòng nhật ký
   *   dầu của vỏ đó, đã gắn phiếu tiền này. Tiền = số tiền phiếu; ngày = ngày chứng từ.
   */
  fuel: z.object({
    containerId: Uuid,
    liters: z.number().positive('Số lít phải lớn hơn 0.'),
  }).optional(),
})

/**
 * 💵 THU TIỀN / 🏦 BÁO CÓ NGAY TRONG PHIẾU CHỨNG TỪ BÁN (`DebtTab` · `collect`, user 17/08) — lập phiếu thu ở quỹ / Báo Có ở
 * tài khoản ngân hàng RỒI đối trừ với hóa đơn, MỘT giao dịch. `type` luôn là `thu`.
 */
export const CollectInvoiceBody = z.object({
  invoiceId: Uuid,
  accountId: Uuid,
  txnDate: ISODate,
  amount: moneyField('Số tiền thu'),
  description: blankAsAbsent(z.string()),
  counterparty: blankAsAbsent(z.string()),
  objectCode: blankAsAbsent(z.string()),
  contraAccount: blankAsAbsent(z.string()),
  postingDate: blankAsAbsent(ISODate),
  note: blankAsAbsent(z.string()),
  /**
   * ⭐ PHIẾU QUẢN TRỊ (DX) — đặt NGAY LÚC LẬP. Trước đây chỉ sửa được sau khi phiếu đã có số, nên
   * mọi phiếu sinh ra ở trạng thái mặc định rồi chờ ai đó nhớ quay lại; quên một lần là một chứng
   * từ sai loại nằm im trong sổ và đẩy sang MISA sai.
   */
  mgmtOnly: z.boolean().optional(),
  /* Ô AMIS của hộp Báo Có / phiếu thu (L25 — bản cũ mở `BankNoteModal` / `CashAddModal` ĐỦ Ô từ chứng từ BH, `Az.tsx:28745-28780`). */
  payeeName: blankAsAbsent(z.string().max(200)),
  expenseItem: blankAsAbsent(z.string().max(50)),
  jobRef: blankAsAbsent(z.string().max(50)),
  amisReason: blankAsAbsent(z.string().max(300)),
  amisEntryDesc: blankAsAbsent(z.string().max(500)),
  /* Phiếu thu QUỸ thêm Địa chỉ · Nhân viên · Kèm theo như `CashAddModal` vế thu (`Finance.tsx:1851-2015`, hd-ban-24). Vế thu bản cũ
     KHÔNG có nhóm hoá đơn / VAT hay loại phiếu — chỉ phiếu chi (`:455`, `:539`, `:1823`). */
  partyAddress: TxnDetailFields.partyAddress,
  staffCode: TxnDetailFields.staffCode,
  attachedDocs: TxnDetailFields.attachedDocs,
})
export type CollectInvoiceBodyT = z.infer<typeof CollectInvoiceBody>


/**
 * SỬA PHIẾU (`0082`). Ô mô tả · AMIS · hóa đơn GTGT sửa lúc nào cũng được; ba ô LÕI (số tiền · ngày
 * giao dịch · tài khoản) chỉ khi dòng còn tự do — xem `TxnCore` ở `@rs/bank`. ⛔ KHÔNG BAO GIỜ có
 * CHIỀU thu/chi. `.strict()`: gửi kèm ô lạ là bị TỪ CHỐI, không phải lặng lẽ bỏ qua.
 */
export const UpdateTxnDetailsBody = z.object({
  version: Version,
  postingDate: clearable(ISODate),
  description: z.string().max(500).optional(),
  counterparty: clearable(z.string().max(300)),
  objectCode: clearable(z.string().max(50)),
  contraAccount: clearable(z.string().max(20)),
  note: clearable(z.string().max(1000)),
  payeeName: clearable(z.string().max(200)),
  partyAddress: clearable(z.string().max(500)),
  staffCode: clearable(z.string().max(50)),
  attachedDocs: clearable(z.number().int().min(0).max(999)),
  expenseItem: clearable(z.string().max(50)),
  jobRef: clearable(z.string().max(50)),
  payKind: clearable(z.enum(['invoice', 'bank', 'other'])),
  hasInvoice: z.boolean().optional(),
  vatRate: clearable(z.number().min(0).max(100)),
  vatAmount: clearable(moneyField('Tiền thuế GTGT')),
  acctVat: clearable(z.string().max(20)),
  invoiceDate: clearable(ISODate),
  invoiceNo: clearable(z.string().max(50)),
  goodsGroup: clearable(z.string().max(100)),
  amisReason: clearable(z.string().max(300)),
  amisEntryDesc: clearable(z.string().max(500)),
  mgmtOnly: z.boolean().optional(),
  /* ── Ba ô LÕI — máy chủ chỉ nhận khi dòng chưa đối trừ / không phải chi ĐNTT / không ghép cặp /
       chưa dựng chứng từ AMIS (`coreLockError`); còn lại trả 4xx kèm câu nói cách gỡ. ── */
  amount: blankAsAbsent(moneyField('Số tiền')),
  txnDate: blankAsAbsent(ISODate),
  accountId: blankAsAbsent(Uuid),
  /** Số chứng từ gõ tay — CHỈ phiếu QUỸ (bản cũ `CashAddModal` sửa được số, `Finance.tsx:1935`); Báo Nợ/Có máy cấp. */
  voucherNo: blankAsAbsent(z.string().trim().max(30, 'Số chứng từ tối đa 30 ký tự.')),
  /** Đổi phiếu chi ↔ phiếu thu — CHỈ phiếu QUỸ còn tự do (bản cũ `CashAddModal` đổi chiều ngay khi sửa, `Finance.tsx:1871`). */
  type: z.enum(['thu', 'chi']).optional(),
  /** Đối ứng 111/112 mà chưa có vế cặp ⇒ lưu là sinh vế cặp ở tài khoản này rồi ghép đôi (`addTransferPair`). */
  pairAccountId: blankAsAbsent(Uuid),
}).strict()
export type UpdateTxnDetailsBodyT = z.infer<typeof UpdateTxnDetailsBody>

/**
 * ⚠ TRẦN 500 DÒNG MỘT LÔ. Sao kê một tháng của một tài khoản hiếm khi quá vài trăm dòng; trần này
 *   giữ một lần nhập nằm gọn trong một giao dịch CSDL thay vì khoá bảng hàng phút.
 */
export const ImportTxnBody = z.object({
  rows: z.array(z.object(TxnFields))
    .min(1, 'Không có dòng nào để nhập.')
    .max(500, 'Tối đa 500 dòng một lần nhập — chia nhỏ sao kê ra.'),
})

export const MatchTxnBody = z.object({
  version: Version,
  invoiceId: Uuid,
  note: blankAsAbsent(z.string()),
  /** ⭐ Dòng CHI VƯỢT phần còn nợ của chứng từ mua — người dùng đã xác nhận ⇒ chỉ cấn phần còn lại (bản cũ `Finance.tsx:3219`). */
  overpayOk: z.boolean().optional(),
})

/**
 * ⭐ 🔗 ĐỐI TRỪ MỘT DÒNG CHI CÓ SẴN VỚI PHIẾU ĐNTT (bản cũ `applyMatch` / `applyCashMatch` nhánh `dntt`, `Finance.tsx:3183-3191` ·
 *   `:3277-3284`): phiếu ĐÃ ĐẶT LỊCH CHI ⇒ trả từng phần theo số tiền dòng; phiếu ĐÃ CHI QUA NGÂN HÀNG bằng UNC chưa có dòng sổ
 *   ⇒ chỉ nối dòng. Kèm SỐ BẢN của phiếu ĐNTT (A4).
 */
export const MatchRequestTxnBody = z.object({
  version: Version,
  requestId: Uuid,
  requestVersion: Version,
  /** Dòng lớn hơn phần còn phải trả — người dùng đã xác nhận ⇒ chỉ cấn phần còn lại, phần dư để riêng (`Finance.tsx:3189`). */
  overpayOk: z.boolean().optional(),
})

/**
 * ⭐ MỘT DÒNG THU CẤN NHIỀU HOÁ ĐƠN BÁN. Khách chuyển một lần cho ba hoá đơn là chuyện thường, mà
 * sao kê ngân hàng thì KHÔNG tách được — nó là một dòng thật.
 * ⚠ Cửa RIÊNG chứ không nới `MatchTxnBody`: đường một-hoá-đơn có `arInvoiceId` trên chính dòng sổ,
 *   đường nhiều-hoá-đơn ghi ở bảng phân bổ. Gộp một thân cho hai đường lưu khác nhau là chỗ để
 *   một hôm nào đó gửi thiếu trường rồi ghi nửa vời.
 */
export const MatchTxnManyBody = z.object({
  version: Version,
  allocations: z.array(z.object({ invoiceId: Uuid, amount: moneyField('Số tiền cấn') }))
    .min(1, 'Chưa chọn hóa đơn nào để cấn trừ.')
    .max(50, 'Tối đa 50 hóa đơn trong một phiếu — tách phiếu ra.'),
  note: blankAsAbsent(z.string()),
})

export const TxnStepBody = z.object({ version: Version })

/**
 * 🗑 Xoá hàng loạt phiếu Sổ phụ / Sổ quỹ (phu-ke-toan-2 + ke-toan-4) — bỏ qua phiếu không xoá được, xoá hết phần còn lại trong
 * MỘT giao dịch. `dryRun` = chỉ xem trước (số phiếu xoá · tổng tiền · phiếu bỏ qua kèm lý do), không ghi gì.
 */
export const TxnBulkDeleteBody = z.object({
  items: z.array(z.object({ id: Uuid, version: Version })).min(1, 'Chưa chọn phiếu nào.').max(500),
  dryRun: z.boolean().optional(),
})
export type TxnBulkDeleteBodyT = z.infer<typeof TxnBulkDeleteBody>

/**
 * 💜 GHI PHẦN CHƯA GẮN CỦA MỘT PHIẾU THU THÀNH CỌC (F15-3, bản cũ `Finance.tsx:3650-3651` — sửa phiếu thu 131 có đối tượng
 *   ⇒ cọc). `customerId` bắt buộc khi phiếu chưa cấn hoá đơn nào; phiếu đã cấn một phần thì cọc thuộc khách đã cấn.
 */
export const TxnAdvanceBody = z.object({
  version: Version,
  customerId: blankAsAbsent(Uuid),
})

export const ListTxnQuery = PageQuery.extend({
  accountId: blankAsAbsent(Uuid),
  type: blankAsAbsent(z.enum(['thu', 'chi'])),
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
  /** `'true'` = chỉ dòng CHƯA đối trừ — hàng chờ của kế toán. */
  unmatched: blankAsAbsent(z.enum(['true', 'false'])),
  /** `'true'` = chỉ dòng CHƯA ghép cặp — danh sách chọn của hộp 🔗 Gắn cặp NH↔TM, TOÀN SỔ (bản cũ `Finance.tsx:5482-5485`). */
  unpaired: blankAsAbsent(z.enum(['true', 'false'])),
  /** Lọc theo phiếu ĐNTT đã sinh ra dòng sổ này (`0121`). */
  paymentRequestId: blankAsAbsent(Uuid),
})

export const PeriodQuery = z.object({
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
})

export const ForecastQuery = z.object({
  asOf: blankAsAbsent(ISODate),
  /** Nhìn trước bao nhiêu ngày. Mặc định 30 — một chu kỳ công nợ của RT. */
  days: z.coerce.number().int().min(1).max(365).default(30),
})

export const TxnIdParams = z.object({ id: Uuid })

/**
 * Ghép dòng sổ `:id` với vế đối ứng `pairId` thành một nghiệp vụ nộp/rút hoặc chuyển nội bộ.
 * `amountMismatchOk` = người bấm đã trả lời "Số tiền 2 phiếu KHÁC NHAU — vẫn gắn cặp?" (bản cũ `Finance.tsx:3024`, K02932):
 * thiếu cờ này mà hai phiếu lệch tiền thì máy chủ từ chối.
 */
export const LinkPairBody = z.object({ pairId: Uuid, amountMismatchOk: z.boolean().optional() })
export type LinkPairBodyT = z.infer<typeof LinkPairBody>

/** ✍ KTT ký điện tử một lô phiếu quỹ (`0137`) — mỗi phiếu mang SỐ BẢN màn đang cầm (A4). */
export const SignTxnBody = z.object({
  items: z.array(z.object({ id: Uuid, version: z.number().int().positive() }))
    .min(1, 'Chọn ít nhất một phiếu.').max(500, 'Tối đa 500 phiếu một lần ký.'),
})
export type SignTxnBodyT = z.infer<typeof SignTxnBody>

/** ⇪ Đẩy AMIS / 🔓 gỡ khoá một lô phiếu Sổ phụ / Sổ quỹ (ke-toan-1, `0187`) — bản cũ đẩy / gỡ theo dòng và theo tick. */
export const TxnAmisBody = z.object({
  ids: z.array(Uuid).min(1, 'Chọn ít nhất một phiếu.').max(500, 'Tối đa 500 phiếu một lần.'),
})
export type TxnAmisBodyT = z.infer<typeof TxnAmisBody>

/**
 * Hai cửa KHÔNG nhận thân request: gỡ ghép (chỉ cần `:id`) và tự vá (kế hoạch tính lại TRONG giao
 * dịch ghi, cố ý không nhận danh sách từ màn — xem `ledger-audit.ts`). `.strict()` vẫn từ chối ô
 * lạ, nên đây là lược đồ thật chứ không phải một cách lách danh sách miễn trừ.
 */
export const LedgerNoBody = z.object({}).strict()

export type CreateTxnBodyT = z.infer<typeof CreateTxnBody>
export type MatchTxnManyBodyT = z.infer<typeof MatchTxnManyBody>
export type MatchTxnBodyT = z.infer<typeof MatchTxnBody>
export type MatchRequestTxnBodyT = z.infer<typeof MatchRequestTxnBody>
export type TxnAdvanceBodyT = z.infer<typeof TxnAdvanceBody>
export type ImportTxnBodyT = z.infer<typeof ImportTxnBody>
export type ListTxnQueryT = z.infer<typeof ListTxnQuery>
export type ForecastQueryT = z.infer<typeof ForecastQuery>

/* ══ KẾ HOẠCH TIỀN TRONG NGÀY (`0075`) ═══════════════════════════════════════════════════════ */

export const SaveCashPlanBody = z.object({
  planDate: blankAsAbsent(ISODate),
  expIn: moneyField('Dự kiến thu'),
  expOut: moneyField('Dự kiến chi'),
  note: blankAsAbsent(z.string()),
})

/** Bỏ trống `from`/`to` ⇒ chỉ lấy kế hoạch của HÔM NAY — đó là thứ dải KPI cần. */
export const CashPlanQuery = z.object({
  from: blankAsAbsent(ISODate),
  to: blankAsAbsent(ISODate),
})

export type SaveCashPlanBodyT = z.infer<typeof SaveCashPlanBody>
export type CashPlanQueryT = z.infer<typeof CashPlanQuery>

/* ══ 📎 CHỨNG TỪ ĐÍNH KÈM MỘT PHIẾU TIỀN (`0128`) ═══════════════════════════════════════════════
   Thân giống cửa đính kèm chung, TRỪ `ownerKind` — cửa này CHỈ đính vào phiếu tiền, và chính vì
   vậy nó gác bằng `finance:write` thay vì `ops:write`. */
export const TxnFileBody = z.object({
  fileName: z.string().min(1, 'Thiếu tên tệp.'),
  mime: z.string().min(1, 'Thiếu loại tệp.'),
  dataBase64: z.string().min(1, 'Tệp rỗng.'),
})
export type TxnFileBodyT = z.infer<typeof TxnFileBody>
